<script setup lang="ts">
import { api } from '@/api';
import type { CreateUpdateUserDto, UserDto } from '@/api/generator';
import { AppPermissionsEnum } from '@/api/generator';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAutocomplete from '@/components/forms/Autocomplete/VeeAutocomplete.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import AvatarImage from '@/components/images/AvatarImage.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import { useAccountStore } from '@/stores/accountStore.ts';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useDeleteAccountWithConfirmation, useRoles } from '../utils';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { MOBILE_IMAGE_WIDTH_DETAIL } from '@/components/images/utils';
import { useFormTab } from '@/components/forms/useFormTab';

const { t } = useTranslation();

const accountStore = useAccountStore();

const slots = defineSlots<{ activationAction(props: unknown): any }>();

const values = useFormValues<UserDto & CreateUpdateUserDto>();
const router = useRouter();
const permissionsStore = usePermissionsStore();
const { action: deleteAction } = useDeleteAccountWithConfirmation();
const mode = useFormMode();
const { rolesOptions, rolesDict } = useRoles();
const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        indicatorState: getIndicatorState([
          'emailAddress',
          'lastName',
          'assignedRoles',
          'attachments',
        ]),
      },
      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || mode.value === 'add',
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);
  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.accounts.edit,
        params: { id: values.value.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                id: values.value.id,
                name: values.value.fullName || '',
                iuid: values.value.iuid,
                typeName: 'UserAccount_one',
              },
              t,
            ),
          });
          if (result?.success) {
            await router.push({ name: routenames.accounts.overview });
          }
        }
      },
    },
  ];
});

const canModifyAvailability = computed(
  () =>
    permissionsStore.hasPermissions([AppPermissionsEnum.GlobalAccountsEdit]) &&
    values.value.id !== accountStore.currentUser?.id, // make sure, that I am not disabled
);

const availabilityMessage = computed(() =>
  !canModifyAvailability.value && mode.value !== 'details'
    ? t('NotChangeableBecauseOfRightsOrSafetyMsg')
    : undefined,
);
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <v-row>
        <v-col cols="12" md="12" sm="12" lg="1">
          <AvatarImage
            :width="MOBILE_IMAGE_WIDTH_DETAIL"
            :pictureId="values.profilePictureId"
            data-testid="accountEntityImage"
          />
        </v-col>
        <v-col cols="12" md="9">
          <v-row>
            <v-col v-if="slots['activationAction']" cols="12">
              <slot name="activationAction"></slot>
            </v-col>
            <v-col cols="12" md="6" sm="4" lg="4">
              <VeeTextField name="title" :label="$t('Title')" />
              <VeeTextField
                class="gutterTop"
                name="lastName"
                :label="$t('LastName')"
                :placeholder="$t('Required')"
              />
              <VeeTextField class="gutterTop" name="firstName" :label="$t('FirstName')" />
              <VeeTextField class="gutterTop" name="middleName" :label="$t('MiddleName')" />
            </v-col>
            <v-col cols="12" md="6" sm="6" lg="4">
              <VeeTextField
                name="emailAddress"
                :label="$t('Email')"
                :placeholder="$t('Required')"
              />
              <VeeTextField class="gutterTop" name="mobileNumber" :label="$t('Mobile')" />
              <VeeTextField class="gutterTop" name="phoneNumber" :label="$t('Phone')" />
            </v-col>
            <v-col cols="12" md="6" sm="6" lg="4">
              <VeeTextarea name="description" :label="$t('Description')" rows="7" />
              <VeeAttachments
                class="mt-1"
                name="attachments"
                data-testid="Description_Attachments"
              />
            </v-col>

            <v-col cols="12">
              <VDivider></VDivider>
              <VeeAutocomplete
                class="gutterTop"
                :items="rolesOptions"
                solo
                chips
                :placeholder="$t('Required')"
                :clearable="true"
                :closableChips="true"
                multiple
                :label="$t('Role_other')"
                name="assignedRoles"
              >
                <template #itemTitle="slotData">
                  <IdentifierLabel
                    :identifier="rolesDict && rolesDict[slotData.value].iuid"
                    :label="slotData.title"
                  ></IdentifierLabel>
                </template>
              </VeeAutocomplete>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="2" sm="">
          <span>
            <VeeAvailability
              name="isAvailable"
              :isReferencedBy="values.isReferencedBy ?? undefined"
              :disabled="!canModifyAvailability"
              :infoMessage="availabilityMessage"
              :impactMessage="$t('DisableAccountMsg')"
              :hideReferences="true"
            />
          </span>
          <EntityChangeBlock v-if="mode === 'details'" class="gutterTop" :entity="values" />
        </v-col>
      </v-row>
    </template>
    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="api.usersApi.getUserHistory.bind(api.usersApi)"
      />
    </template>
  </BasePageNavigation>
</template>
